import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import DocPreviewReveal from './components/DocPreviewReveal';

/**
 * Página pública da prévia do documento, aberta pelo link do e-mail.
 *
 * O e-mail não roda JavaScript, então a experiência de "ver nítido e depois
 * desfocar" mora aqui. O token é opaco e tem validade curta; o payload vem do
 * `GET /doc-preview/:token` no apiV2.
 */

const BASE_URL = (process.env.REACT_APP_API_URL || window.location.origin).replace(/\/+$/, '');
const CHAT_URL = 'https://consultamais.app/consulta/chat';

const DEMO_DOC = {
  docType: 'atestado',
  patientName: 'Maria Aparecida de Souza',
  patientCpf: '123.456.789-00',
  patientPhone: '(41) 99999-0000',
  emittedAt: '15/08/2026 20:29 (Horário de Brasília)',
  daysOff: 2,
  startDate: '15/08/2026',
  cid: 'R51.9',
};

export default function PreviaDocumento() {
  const { token } = useParams();
  const [search] = useSearchParams();
  const isDemo = search.get('demo') === '1';

  const [state, setState] = useState(isDemo ? 'ready' : 'loading');
  const [doc, setDoc] = useState(isDemo ? DEMO_DOC : null);
  const [priceLabel, setPriceLabel] = useState('R$ 79,90');

  useEffect(() => {
    if (isDemo || !token) return;
    let alive = true;
    (async () => {
      try {
        const res = await fetch(`${BASE_URL}/doc-preview/${encodeURIComponent(token)}`);
        if (!alive) return;
        if (res.status === 404 || res.status === 410) {
          setState('expired');
          return;
        }
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (!alive) return;
        setDoc(data.doc || null);
        if (data.priceLabel) setPriceLabel(data.priceLabel);
        setState(data.doc ? 'ready' : 'expired');
      } catch {
        if (alive) setState('error');
      }
    })();
    return () => {
      alive = false;
    };
  }, [token, isDemo]);

  return (
    <div style={{ minHeight: '100vh', background: '#F1F5F9', padding: '1.25rem 0.75rem 3rem' }}>
      <div style={{ maxWidth: 640, margin: '0 auto' }}>
        <h1 style={{ fontSize: '1.15rem', color: '#0F172A', margin: '0 0 0.35rem', textAlign: 'center' }}>
          Seu documento está pronto
        </h1>
        <p
          style={{
            margin: '0 0 1rem',
            fontSize: '0.85rem',
            color: '#475569',
            lineHeight: 1.5,
            textAlign: 'center',
          }}
        >
          O médico revisou e liberou a emissão. Falta apenas a confirmação do pagamento
          para o documento oficial ser assinado e enviado ao seu e-mail.
        </p>

        {state === 'loading' ? (
          <p style={{ textAlign: 'center', color: '#64748B', fontSize: '0.9rem' }}>Carregando…</p>
        ) : null}

        {state === 'expired' ? (
          <p style={{ textAlign: 'center', color: '#64748B', fontSize: '0.9rem' }}>
            Este link expirou. Abra o chat da sua consulta para continuar.
          </p>
        ) : null}

        {state === 'error' ? (
          <p style={{ textAlign: 'center', color: '#64748B', fontSize: '0.9rem' }}>
            Não foi possível carregar a prévia agora. Abra o chat da sua consulta para continuar.
          </p>
        ) : null}

        {state === 'ready' && doc ? (
          <DocPreviewReveal
            doc={doc}
            priceLabel={priceLabel}
            unlockHref={CHAT_URL}
            revealKey={token || null}
          />
        ) : null}

        {state !== 'ready' ? (
          <div style={{ textAlign: 'center', marginTop: '1rem' }}>
            <a
              href={CHAT_URL}
              style={{
                display: 'inline-block',
                padding: '0.8rem 1.4rem',
                borderRadius: 999,
                background: '#D32F2F',
                color: '#fff',
                fontWeight: 800,
                textDecoration: 'none',
              }}
            >
              Abrir o chat da consulta
            </a>
          </div>
        ) : null}

        <p
          style={{
            margin: '1rem 0 0',
            fontSize: '0.72rem',
            color: '#64748B',
            lineHeight: 1.55,
            textAlign: 'center',
          }}
        >
          A folha acima reproduz o layout do documento que você vai receber. Ela não é o documento:
          não possui assinatura digital ICP-Brasil, o QR Code é inválido e não tem validade legal.
          O documento oficial é emitido após a confirmação do pagamento.
        </p>
      </div>
    </div>
  );
}
